import React, { InputHTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';

/**
 * Local-state text value with one-way external sync.
 *
 * The text a user is typing lives HERE, updated synchronously on every
 * keystroke, and is pushed outward through `onValueChange`. The external
 * `value` is adopted only while the field is not focused. That is the whole
 * fix for dropped characters on the list surfaces: there the external value
 * round-trips through the URL (useSearchParams), which React Router applies
 * asynchronously, so a keystroke landing mid-round-trip used to re-render the
 * old value into the controlled input and the next event was built on it.
 *
 * External changes still land whenever they come from somewhere else — a deep
 * link on load, the Reset button, dismissing a chip — because every one of
 * those moves focus off the field first.
 */
export const useLocalTextValue = (
  value: string,
  onValueChange: (next: string) => void,
  sanitize?: (raw: string) => string,
) => {
  const [local, setLocal] = useState(value);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setLocal(value);
  }, [value]);

  const onChange = useCallback(
    (raw: string) => {
      const next = sanitize ? sanitize(raw) : raw;
      setLocal(next);
      onValueChange(next);
    },
    [onValueChange, sanitize],
  );
  const onFocus = useCallback(() => {
    focusedRef.current = true;
  }, []);
  const onBlur = useCallback(() => {
    focusedRef.current = false;
  }, []);

  return { local, onChange, onFocus, onBlur };
};

type LocalTextInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  value: string;
  onValueChange: (next: string) => void;
  /** Applied to every keystroke before it is shown or emitted (digits only, etc.). */
  sanitize?: (raw: string) => string;
};

/** A controlled `<input type="text">` driven by useLocalTextValue. */
export const LocalTextInput: React.FC<LocalTextInputProps> = ({
  value,
  onValueChange,
  sanitize,
  onFocus,
  onBlur,
  type = 'text',
  ...rest
}) => {
  const field = useLocalTextValue(value, onValueChange, sanitize);
  return (
    <input
      {...rest}
      type={type}
      value={field.local}
      onChange={(e) => field.onChange(e.target.value)}
      onFocus={(e) => {
        field.onFocus();
        onFocus?.(e);
      }}
      onBlur={(e) => {
        field.onBlur();
        onBlur?.(e);
      }}
    />
  );
};

export default LocalTextInput;
